import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, EMPTY, Observable, of, Subject, timer} from 'rxjs';
import {delay, expand, filter, finalize, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {ECCommunityComputationStatus, ECComputedCommunityEconomics} from 'smag-commons/dto/eco';
import {SmagApiService} from '../api/smag-api.service';
import * as moment from 'moment-timezone';

@Injectable({
    providedIn: 'root'
})
export class ComputationStatusService implements OnDestroy {

    private destroyed = new Subject<void>();
    private showComputationBanner: BehaviorSubject<boolean>;

    constructor(private apiService: SmagApiService) {
        this.showComputationBanner = new BehaviorSubject<boolean>(false);
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    /**
     * Emits whenever a computation repeats.
     * This means it doesn't trigger if computation is not in progress.
     */
    get showComputingWarning(): Observable<boolean> {
        return this.showComputationBanner.asObservable();
    }

    // Calls API to know whether the ec economics computation is in progress, missing or finished.
    // If it's in progress, it waits 2000ms before calling API again (unless it's more than 15 mins old).
    // If the computation is complete (i.e. has "response.status" === ECCommunityComputationStatus.COMPUTED), it returns the response
    // If referenceTimestamp is provided, also checks that computation started_on > referenceTimestamp
    checkComputationStatus(entityId: string, year: number, referenceTimestamp?: number, maxTimeout?: number, tz?: string): Observable<ECComputedCommunityEconomics> {
        // wait 500ms before starting
        return timer(500).pipe(
            switchMap(() => this.getEconomicsStatus(entityId, year).pipe(
                expand(response =>
                    this.checkForStatusAndTimestamp(response, referenceTimestamp, maxTimeout, tz) ?
                        EMPTY :  // stop recursion
                        of(response).pipe(
                            delay(2000),
                            tap(() => this.showComputationBanner.next(true)),
                            switchMap(() => this.getEconomicsStatus(entityId, year)))),
                filter(response => this.checkForStatusAndTimestamp(response, referenceTimestamp, maxTimeout, tz)), // continue only if value is either computed or missing or old
                take(1),
                finalize(() => setTimeout(() => this.showComputationBanner.next(false), 2000)))), // after 2 sec, tell listeners to stop showing warning banner
            takeUntil(this.destroyed));
    }

    private getEconomicsStatus(entityId: string, year: number) {
        return this.apiService.ecEconomicsStatus(entityId, year);
    }

    // the computation is considered "ok" if:
    // - status is computed
    // - status is missing
    // - status is in progress or scheduled, but the timestamp is older than 15 minutes
    // if referenceTimestamp is provided, computation must have started after that timestamp (with second precision to account for client-server clock skew)
    // if maxTimeout is provided, computation is completed, and it started more than a minute ago, then stop waiting
    private checkForStatusAndTimestamp(response: ECComputedCommunityEconomics, referenceTimestamp?: number, maxTimeout?: number, tz?: string): boolean {
        const isComputed = response.status === ECCommunityComputationStatus.COMPUTED;
        const isMissing = response.status === ECCommunityComputationStatus.MISSING;
        const isTooOld = (response.status === ECCommunityComputationStatus.IN_PROGRESS || response.status === ECCommunityComputationStatus.SCHEDULED) && this.isTimestampOlderThan15Mins(response.started_on);
        const isStatusOk = isComputed || isMissing || isTooOld;

        // if no reference timestamp provided, only check status
        if (referenceTimestamp === undefined) {
            return isStatusOk;
        }

        // if reference timestamp provided, also verify that computation started after it
        // use second precision to account for client-server clock skew
        const startedOnSeconds = Math.floor(response.started_on / 1000);
        const referenceSeconds = Math.floor(referenceTimestamp / 1000);
        if (isStatusOk && startedOnSeconds >= referenceSeconds) {
            return true;
        }

        // check if max timeout has been exceeded (when status is ok)
        if (isStatusOk && maxTimeout !== undefined && tz !== undefined) {
            const now = moment.tz(tz).valueOf();
            const elapsedTime = now - response.started_on;
            if (elapsedTime >= maxTimeout) {
                return true;
            }
        }

        return false;
    }

    private isTimestampOlderThan15Mins(timestamp: number): boolean {
        return timestamp < (Date.now() - 900000);
    }
}
